import { useEffect, useRef } from 'react';
import { createAsyncStatusHandler } from 'src/common/utils/asyncStatusHandler';
import { InitiateAsyncTaskResponse } from 'src/utilities/useAsyncDownloadTask/useAsyncDownloadTask.utils';
import { IUseAsyncTask, useAsyncTask } from 'src/utilities/useAsyncTask';

export interface UseAsyncTaskHandlerProps {
  onSuccess?: () => Promise<void>;
  onError?: () => Promise<void>;
}

// useAsyncTaskHandler is a custom hook that enhances useAsyncTask by adding onSuccess and onError callbacks.
// To use it, pass an object to useAsyncTaskHandler with onSuccess and onError properties, each being async functions to be executed in success or error cases.
// The function startAsyncTask is used to initiate the async task.
// The API for this hook is the same as for the useAsyncTask hook.

export const useAsyncTaskHandler = ({
  onSuccess,
  onError,
}: UseAsyncTaskHandlerProps): IUseAsyncTask => {
  const { startAsyncTask, status, ...asyncTaskProps } = useAsyncTask();
  const resolvablePromise =
    useRef<(value: void | PromiseLike<void>) => void>(null);

  const handleAsyncStatus = createAsyncStatusHandler(
    handleSuccess,
    handleError,
    resolvablePromise.current
  );

  useEffect(() => {
    handleAsyncStatus(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function startAsyncTaskWithResolvablePromise(
    asyncTaskFunction: () => Promise<InitiateAsyncTaskResponse>
  ) {
    await startAsyncTask(async () => {
      return asyncTaskFunction();
    });

    // use a promise to allow form 'isSubmitting' to handle button disabled state
    await new Promise<void>((resolve) => {
      // resolve resolvablePromise when async task is completed
      resolvablePromise.current = resolve;
    });
  }

  async function handleSuccess(): Promise<void> {
    await onSuccess?.();
  }

  async function handleError(): Promise<void> {
    await onError?.();
  }

  return {
    ...asyncTaskProps,
    status,
    startAsyncTask: startAsyncTaskWithResolvablePromise,
  };
};
